import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Flag, Zap, Target, Sparkles, Circle } from "lucide-react";
import { useTheme } from "next-themes";

type SportsLoaderProps = {
  message?: string;
  variant?: "fullscreen" | "section" | "inline";
  size?: "sm" | "md" | "lg";
  showParticles?: boolean;
  sport?: "football" | "soccer" | "generic";
};

const sizeMap = {
  sm: 40,
  md: 64,
  lg: 96,
};

export const SportsLoader: React.FC<SportsLoaderProps> = ({
  message = "Loading...",
  variant = "section",
  size = "md",
  showParticles = true,
  sport = "football",
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const pixel = sizeMap[size];

  // Gen Z color scheme
  const primaryColor = 'from-green-400 to-green-600';
    
  const accentColor = 'from-green-300 to-green-500';

  const wrapperClass =
    variant === "fullscreen"
      ? "flex min-h-screen items-center justify-center relative overflow-hidden"
      : variant === "inline"
      ? "inline-flex items-center gap-3"
      : "flex items-center justify-center py-12 relative";

  const particleVariants = {
    initial: { opacity: 0, scale: 0 },
    animate: { 
      opacity: [0, 1, 0],
      scale: [0, 1, 0],
      y: [-20, -40, -60],
    },
    transition: {
      duration: 2,
      repeat: Infinity,
      repeatDelay: Math.random() * 2,
    },
  };

  const fieldLineVariants = {
    initial: { pathLength: 0, opacity: 0 },
    animate: { 
      pathLength: 1, 
      opacity: 1,
      transition: { duration: 1.5, ease: "easeInOut" }
    },
  };

  const ballVariants = {
    initial: { scale: 0, rotate: 0 },
    animate: {
      scale: 1,
      rotate: 360,
      transition: {
        scale: { type: "spring", stiffness: 200, damping: 15 },
        rotate: { duration: 2, ease: "linear", repeat: Infinity },
      },
    },
  };

  const glowVariants = {
    initial: { scale: 0.8, opacity: 0 },
    animate: {
      scale: [0.8, 1.2, 0.8],
      opacity: [0, 0.3, 0],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  return (
    <AnimatePresence>
      <div className={wrapperClass}>
        {/* Animated background particles */}
        {showParticles && variant !== "inline" && (
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className={`absolute w-2 h-2 rounded-full bg-gradient-to-r ${accentColor}`}
                style={{
                  left: `${20 + (i * 15)}%`,
                  top: `${30 + (i * 10)}%`,
                }}
                variants={particleVariants}
                initial="initial"
                animate="animate"
                transition={{ delay: i * 0.3 }}
              />
            ))}
          </div>
        )}

        <div className={variant === "inline" ? "flex items-center gap-3" : "flex flex-col items-center"}>
          {/* Main loading animation container */}
          <div className="relative" style={{ width: pixel + 40, height: pixel + 40 }}>
            {/* Outer glow effect */}
            <motion.div
              className={`absolute inset-0 rounded-full bg-gradient-to-r ${primaryColor} blur-xl`}
              variants={glowVariants}
              initial="initial"
              animate="animate"
            />

            {/* Rotating outer ring */}
            <motion.div
              className={`absolute inset-0 rounded-full border-4 bg-gradient-to-r ${primaryColor} border-transparent`}
              style={{ borderStyle: 'solid' }}
              animate={{ rotate: 360 }}
              transition={{ 
                rotate: { 
                  duration: 1.5, 
                  repeat: Infinity, 
                  ease: "linear" 
                } 
              }}
            />

            {/* Football field background */}
            <motion.div
              className={`absolute inset-4 rounded-full bg-gradient-to-br ${
                isDark 
                  ? 'from-gray-800 to-gray-900' 
                  : 'from-white to-gray-100'
              } shadow-lg`}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 20 }}
            >
              {/* Field lines */}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100">
                <motion.path
                  d="M 50 10 L 50 90 M 20 30 L 80 30 M 20 70 L 80 70"
                  stroke={isDark ? "#10b981" : "#fb923c"}
                  strokeWidth="1"
                  fill="none"
                  variants={fieldLineVariants}
                  initial="initial"
                  animate="animate"
                />
                <motion.circle
                  cx="50"
                  cy="50"
                  r="15"
                  stroke={isDark ? "#10b981" : "#fb923c"}
                  strokeWidth="1"
                  fill="none"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 0.5 }}
                />
              </svg>

              {/* Center ball */}
              <motion.div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                variants={ballVariants}
                initial="initial"
                animate="animate"
              >
                <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${primaryColor} flex items-center justify-center shadow-lg`}>
                  <span className="text-white text-lg font-bold">⚽</span>
                </div>
              </motion.div>
            </motion.div>

            {/* Orbiting elements */}
            <motion.div
              className="absolute inset-0"
              animate={{ rotate: 360 }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
            >
              {/* Trophy */}
              <motion.div
                className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2"
                whileHover={{ scale: 1.2, rotate: 15 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className={`p-2 rounded-full bg-gradient-to-br ${accentColor} shadow-lg`}>
                  <Trophy className="h-4 w-4 text-white" />
                </div>
              </motion.div>

              {/* Target */}
              <motion.div
                className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2"
                whileHover={{ scale: 1.2, rotate: -15 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className={`p-2 rounded-full bg-gradient-to-br ${accentColor} shadow-lg`}>
                  <Target className="h-4 w-4 text-white" />
                </div>
              </motion.div>

              {/* Zap */}
              <motion.div
                className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2"
                whileHover={{ scale: 1.2, rotate: 10 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className={`p-2 rounded-full bg-gradient-to-br ${accentColor} shadow-lg`}>
                  <Zap className="h-4 w-4 text-white" />
                </div>
              </motion.div>

              {/* Flag */}
              <motion.div
                className="absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2"
                whileHover={{ scale: 1.2, rotate: -10 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className={`p-2 rounded-full bg-gradient-to-br ${accentColor} shadow-lg`}>
                  <Flag className="h-4 w-4 text-white" />
                </div>
              </motion.div>
            </motion.div>

            {/* Sparkle effects */}
            {showParticles && (
              <>
                <motion.div
                  className="absolute top-1/4 left-1/4"
                  animate={{ 
                    scale: [0, 1, 0],
                    opacity: [0, 1, 0],
                    rotate: [0, 180, 360]
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    delay: 0,
                    ease: "easeInOut"
                  }}
                >
                  <Sparkles className={`h-3 w-3 text-green-400`} />
                </motion.div>

                <motion.div
                  className="absolute top-3/4 right-1/4"
                  animate={{ 
                    scale: [0, 1, 0],
                    opacity: [0, 1, 0],
                    rotate: [0, -180, -360]
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    delay: 1,
                    ease: "easeInOut"
                  }}
                >
                  <Sparkles className={`h-3 w-3 text-orange-400`} />
                </motion.div>
              </>
            )}
          </div>

          {/* Message */}
          {message && (
            <motion.div
              className={variant === "inline" ? "" : "mt-6 text-center"}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <p className={`text-sm font-medium bg-gradient-to-r ${
                isDark ? 'text-green-400' : 'text-orange-600'
              }`}>
                {message}
              </p>
              {variant !== "inline" && (
                <div className="flex items-center justify-center mt-2 gap-1">
                  <motion.div
                    className="w-1 h-1 rounded-full bg-current"
                    animate={{ opacity: [0.3, 1, 0.3] }}
                    transition={{ duration: 1.5, repeat: Infinity, delay: 0 }}
                  />
                  <motion.div
                    className="w-1 h-1 rounded-full bg-current"
                    animate={{ opacity: [0.3, 1, 0.3] }}
                    transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}
                  />
                  <motion.div
                    className="w-1 h-1 rounded-full bg-current"
                    animate={{ opacity: [0.3, 1, 0.3] }}
                    transition={{ duration: 1.5, repeat: Infinity, delay: 0.6 }}
                  />
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </AnimatePresence>
  );
};

export default SportsLoader;
